import { getFieldValue } from "./format";
import { compareValues } from "../store/local-query";

export const treeKey = key => String(key);

export function treeKeySet(keys) {
  return new Set((keys || []).map(treeKey));
}

function createNode(key, data) {
  return { key, data, parent: null, children: [], level: 0, visible: true, hasChildren: false };
}

export function collectNodes(root) {
  const nodes = [];
  const visit = (parent, level) => {
    parent.children.forEach(child => {
      child.level = level;
      nodes.push(child);
      visit(child, level + 1);
    });
  };

  visit(root, 0);

  return nodes;
}

export function buildTree(items, { keyExpr, parentIdExpr, rootValue }) {
  const rootKey = treeKey(rootValue);
  const root = { ...createNode(rootValue, undefined), level: -1, isRoot: true };
  const created = new Map();

  items.forEach(data => {
    const node = createNode(getFieldValue(data, keyExpr), data);

    created.set(treeKey(node.key), node);
  });

  created.forEach(node => {
    const parentValue = getFieldValue(node.data, parentIdExpr);
    const parentKey = parentValue == null ? rootKey : treeKey(parentValue);
    const parent = parentKey === rootKey ? root : created.get(parentKey);

    if (!parent || parent === node) return;

    node.parent = parent;
    parent.children.push(node);
  });

  const nodes = collectNodes(root);

  return { root, nodes, byKey: new Map(nodes.map(node => [treeKey(node.key), node])) };
}

export function sortTree(root, sort, getGetter) {
  if (!sort?.length) return;

  const info = sort.map(item => ({ getter: getGetter(item.selector), desc: !!item.desc }));
  const order = list =>
    list
      .map((node, index) => ({ node, index }))
      .sort((a, b) => {
        for (let i = 0; i < info.length; i += 1) {
          const { getter, desc } = info[i];
          const result = compareValues(getter(a.node.data), getter(b.node.data));

          if (result !== 0) return desc ? -result : result;
        }

        return a.index - b.index;
      })
      .map(entry => entry.node);

  const visit = node => {
    if (node.children.length > 1) node.children = order(node.children);
    node.children.forEach(visit);
  };

  visit(root);
}

function showDescendants(node) {
  node.children.forEach(child => {
    child.visible = true;
    showDescendants(child);
  });
}

function showAncestors(node) {
  const { parent } = node;

  if (!parent || parent.isRoot || parent.visible) return;

  parent.visible = true;
  showAncestors(parent);
}

export function filterTree(nodes, predicate, mode = "withAncestors") {
  if (!predicate) {
    nodes.forEach(node => {
      node.visible = true;
    });
    return;
  }

  nodes.forEach(node => {
    node.visible = false;
  });

  const matched = nodes.filter(node => predicate(node.data));

  matched.forEach(node => {
    node.visible = true;

    if (mode !== "matchOnly") showAncestors(node);
  });

  if (mode === "fullBranch") matched.forEach(showDescendants);
}

export function markHasChildren(node) {
  let hasChildren = false;

  node.children.forEach(child => {
    markHasChildren(child);

    if (child.visible || child.hasChildren) hasChildren = true;
  });

  node.hasChildren = hasChildren;

  return hasChildren;
}

export function flattenTree(root, isExpanded) {
  const rows = [];
  const visit = node => {
    node.children.forEach(child => {
      if (child.visible) rows.push(child);
      if (child.hasChildren && (!child.visible || isExpanded(child.key))) visit(child);
    });
  };

  visit(root);

  return rows;
}

export function parentKeysOf(nodes) {
  return nodes.filter(node => node.visible && node.hasChildren).map(node => treeKey(node.key));
}
